import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { medicareApiClient } from 'clients/medicareApiClient'
import { AxiosError } from 'axios'
import { ILoadingStatus } from 'models/LoadingStatus'

// The body of every case reducer is implicitly wrapped with a call to
// `produce()` from the [immer](https://github.com/mweststrate/immer) library.
// This means that rather than returning a new state object, you can also
// mutate the passed-in state object directly; these mutations will then be
// automatically and efficiently translated into copies, giving you both
// convenience and immutability.
// CreateSlice uses CreateReducer which used immer produce

export interface IMedicareComplaintReducer extends IMedicareComplaintAction {
  status?: ILoadingStatus
  type?: 'VERIFIED' | 'UNVERIFIED' // payload submission only
}

interface IMedicareComplaintGetter {
  mcf: IMedicareComplaintReducer
}

export interface IBeneficiaryPlanData {
  choiceId: string
  planName: string
}
export type IEntType = 'A' | 'B'

export interface IMedicareComplaintAction {
  // step 1
  mbi?: string; // TODO - call this mbi
  coverageStartDate?: { // onFormSubmit, convert to UTC string using date-fns
    day?: number,
    month: number,
    year: number
  };
  lastName?: string // step 2a
  dateOfBirth?: { // onFormSubmit, convert to UTC string using date-fns
    day: number,
    month: number,
    year: number,
  };
  zipCode?: string; // step 2a
  entType?: IEntType; // payload submission only
  // step 2
  emailAddress?: string // step2b
  preferredLanguage?: 'en' | 'es' // step2b
  preferredCallbackNumber?: string; // step2b
  preferredCallbackTime?: 'AM' | 'PM' | 'ANY' // step2b
  issueConcerningPlan?: 'YES' | 'NO'; // step2b
  // planType?: 'medicare' | 'prescription' | 'supplemental' // step2b
  noteIssues?: string; // step2b
  // Step 2a specific
  firstName?: string;
  planName?: string;
  planContractId?: string;
}

export const submitStep1 = createAsyncThunk(
  'messages/submitStep1',
  async (payload: IMedicareComplaintReducer, { rejectWithValue }) => {
    const { coverageStartDate, dateOfBirth, ...basePayload } = payload
    // Clone the original payload
    const response: IMedicareComplaintReducer = {
      ...basePayload,
      coverageStartDate: coverageStartDate ? { ...coverageStartDate } : undefined,
      dateOfBirth: dateOfBirth ? { ...dateOfBirth } : undefined
    }
    // Pending addCase is executed, blocking I/O synchronously
    try {
      const { status, ...newPayload } = payload
      const result = await medicareApiClient.asyncSubmitStep1(newPayload)
      // Insert email address
      response.emailAddress = result.emailAddress
    } catch (unknownError) { // TODO add handling for errors
      const error = unknownError as AxiosError
      if (!(error as AxiosError).response) {
        throw error
      }
      rejectWithValue(error?.response?.data)
      throw unknownError
    }
    return response // fulfilled
  })
export const submitStep2 = createAsyncThunk(
  'messages/submitStep2',
  async (payload: IMedicareComplaintReducer, { getState, rejectWithValue }) => {
    try {
      const { status, ...values } = ((getState() as any).mcf as IMedicareComplaintReducer)
      const newPayload = {
        ...values,
        ...payload
      }
      await medicareApiClient.asyncSubmitStep2(newPayload)
    } catch (unknownError) { // TODO add handling for errors
      const error = unknownError as AxiosError
      if (!(error as AxiosError).response) {
        throw error
      }
      rejectWithValue(error?.response?.data)
    }
    return payload
  }
)
export const submitStep2b = createAsyncThunk(
  'messages/submitStep2b',
  async (payload: IMedicareComplaintReducer, { rejectWithValue }) => {
    try {
      const { status, ...newPayload } = payload
      await medicareApiClient.asyncSubmitStep2b(newPayload)
    } catch (unknownError) { // TODO add handling for errors
      const error = unknownError as AxiosError
      if (!(error as AxiosError).response) {
        throw error
      }
      rejectWithValue(error?.response?.data)
    }
    return payload
  }
)

export const mcfSlice = createSlice({
  name: 'mcf',
  initialState: {
    status: undefined,
    mbi: undefined,
    coverageStartDate: undefined,
    lastName: undefined, // step 2a
    dateOfBirth: undefined,
    zipCode: undefined, // step 2a
    entType: 'A' as IEntType,
    // step 2
    emailAddress: undefined, // step2b
    preferredLanguage: undefined, // step2b
    preferredCallbackNumber: undefined, // step2b
    preferredCallbackTime: undefined, // step2b
    issueConcerningPlan: undefined, // step2b
    // planType: undefined, // step2b
    noteIssues: undefined, // step2b
    // Step 2a specific
    firstName: undefined,
    planName: undefined,
    planContractId: undefined,
    // other
    beneficiaryPlanData: undefined
  },
  reducers: {
    resetMcfState: (state: IMedicareComplaintReducer) => {
      state.mbi = undefined
      state.coverageStartDate = undefined
      state.lastName = undefined
      state.dateOfBirth = undefined
      state.zipCode = undefined
      // step 2
      state.emailAddress = undefined // step2b
      state.preferredLanguage = undefined // step2b
      state.preferredCallbackNumber = undefined // step2b
      state.preferredCallbackTime = undefined // step2b
      state.issueConcerningPlan = undefined // step2b
      // state.planType = undefined // step2b
      state.noteIssues = undefined // step2b
      // Step 2a specific
      state.firstName = undefined
      state.planName = undefined
      state.planContractId = undefined
    },
    resetLoadingState: (state: IMedicareComplaintReducer) => {
      state.status = undefined
    },
    setLoadingState: (state: IMedicareComplaintReducer, action: PayloadAction<ILoadingStatus>) => { // used for jest tests only
      state.status = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitStep1.pending, (state: IMedicareComplaintReducer, { meta }) => {
        state.status = 'loading'
        state.mbi = meta.arg.mbi
        state.coverageStartDate = meta.arg.coverageStartDate
        state.firstName = meta.arg.firstName
        state.lastName = meta.arg.lastName
        state.dateOfBirth = meta.arg.dateOfBirth
        state.zipCode = meta.arg.zipCode
        state.entType = meta.arg.entType
      })
      .addCase(submitStep1.fulfilled, (state: IMedicareComplaintReducer, action: PayloadAction<IMedicareComplaintAction>) => {
        state.emailAddress = action.payload.emailAddress
        state.mbi = action.payload.mbi
        state.coverageStartDate = action.payload.coverageStartDate
        state.firstName = action.payload.firstName
        state.lastName = action.payload.lastName
        state.dateOfBirth = action.payload.dateOfBirth
        state.zipCode = action.payload.zipCode
        state.entType = action.payload.entType
        state.status = 'finished'
      })
      .addCase(submitStep1.rejected, (state: IMedicareComplaintReducer) => {
        state.status = 'error'
      })
      .addCase(submitStep2.pending, (state: IMedicareComplaintReducer, { meta }) => {
        state.status = 'loading'
        state.emailAddress = meta.arg.emailAddress
        state.preferredLanguage = meta.arg.preferredLanguage
        state.preferredCallbackNumber = meta.arg.preferredCallbackNumber
        state.preferredCallbackTime = meta.arg.preferredCallbackTime
        state.issueConcerningPlan = meta.arg.issueConcerningPlan
        state.planName = meta.arg.planName
        state.planContractId = meta.arg.planContractId
        state.noteIssues = meta.arg.noteIssues
      })
      .addCase(submitStep2.fulfilled, (state: IMedicareComplaintReducer, action: PayloadAction<IMedicareComplaintAction>) => {
        state.emailAddress = action.payload.emailAddress
        state.preferredLanguage = action.payload.preferredLanguage
        state.preferredCallbackNumber = action.payload.preferredCallbackNumber
        state.preferredCallbackTime = action.payload.preferredCallbackTime
        state.issueConcerningPlan = action.payload.issueConcerningPlan
        state.planName = action.payload.planName
        state.planContractId = action.payload.planContractId
        state.noteIssues = action.payload.noteIssues
        state.status = 'finished'
      })
      .addCase(submitStep2.rejected, (state: IMedicareComplaintReducer) => {
        state.status = 'error'
      })
      .addCase(submitStep2b.pending, (state: IMedicareComplaintReducer, { meta }) => {
        state.status = 'loading'
        state.firstName = meta.arg.firstName
        state.lastName = meta.arg.lastName
        state.zipCode = meta.arg.zipCode
        state.emailAddress = meta.arg.emailAddress
        state.preferredLanguage = meta.arg.preferredLanguage
        state.preferredCallbackNumber = meta.arg.preferredCallbackNumber
        state.preferredCallbackTime = meta.arg.preferredCallbackTime
        state.issueConcerningPlan = meta.arg.issueConcerningPlan
        state.noteIssues = meta.arg.noteIssues
        state.planName = meta.arg.planName
        state.planContractId = meta.arg.planContractId
      })
      .addCase(submitStep2b.fulfilled, (state: IMedicareComplaintReducer, action: PayloadAction<IMedicareComplaintAction>) => {
        state.firstName = action.payload.firstName
        state.lastName = action.payload.lastName
        state.zipCode = action.payload.zipCode
        state.emailAddress = action.payload.emailAddress
        state.preferredLanguage = action.payload.preferredLanguage
        state.preferredCallbackNumber = action.payload.preferredCallbackNumber
        state.preferredCallbackTime = action.payload.preferredCallbackTime
        state.issueConcerningPlan = action.payload.issueConcerningPlan
        state.noteIssues = action.payload.noteIssues
        state.planName = action.payload.planName
        state.planContractId = action.payload.planContractId
        state.status = 'finished'
      })
      .addCase(submitStep2b.rejected, (state: IMedicareComplaintReducer) => {
        state.status = 'error'
      })
  }
})

export const {
  resetMcfState,
  resetLoadingState,
  setLoadingState
} = mcfSlice.actions

// GETTERS
export const getFormData = (state: IMedicareComplaintGetter) => {
  return state.mcf
}

export const getLoadingState = (state: IMedicareComplaintGetter) => {
  return state.mcf.status
}

export default mcfSlice.reducer
